import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { SuperAdminLayout } from "@/components/SuperAdminLayout";
import {
  ShoppingBag,
  Search,
  Filter,
  Download,
  RefreshCw,
  DollarSign,
  TrendingUp,
  Package,
  XCircle,
  ArrowLeft,
} from "lucide-react";
import { toast } from "react-hot-toast";

const API = process.env.REACT_APP_BACKEND_URL || "";

const getAdminAuthHeader = () => {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function SuperAdminOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const LIMIT = 10;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchData(1);
  }, []);

  const fetchData = async (pageNumber = 1) => {
    try {
      setLoading(true);

      const skip = (pageNumber - 1) * LIMIT;

      const ordersRes = await axios.post(
        `${API}/api/super-admin/orders/search`,
        {
          skip, // ✅ REQUIRED
          limit: LIMIT, // ✅ OPTIONAL (but safe)
        },
        {
          headers: getAdminAuthHeader(),
        }
      );

      setOrders(ordersRes.data.data);
      setTotal(ordersRes.data.total);
      setPage(pageNumber);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      confirmed: "bg-blue-100 text-blue-800",
      preparing: "bg-purple-100 text-purple-800",
      ready: "bg-green-100 text-green-800",
      delivered: "bg-teal-100 text-teal-800",
      completed: "bg-gray-100 text-gray-800",
      cancelled: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-semibold ${
          styles[status] || styles.pending
        }`}
      >
        {status?.toUpperCase()}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <SuperAdminLayout>
      <div>
        {/* Page Header */}
        <div
          className="mb-8 flex items-center gap-4 w-full  px-6
  bg-gradient-to-r from-orange-500 to-red-500
  shadow-md text-white py-[1.65rem]"
        >
          <Button
            onClick={() => navigate("/admin/dashboard")}
            variant="ghost"
            className="hover:bg-gray-100"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold ">Order Monitoring</h1>
            <p className=" mt-1">Track orders across all restaurant outlets</p>
          </div>
        </div>
        <div className="p-8">
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Total Orders
                  </CardTitle>
                  <ShoppingBag className="h-5 w-5 text-gray-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">
                    {stats.total_orders}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">All time</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Total Revenue
                  </CardTitle>
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600">
                    {formatCurrency(stats.total_revenue)}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Gross revenue</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Commission Earned
                  </CardTitle>
                  <TrendingUp className="h-5 w-5 text-gray-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-600">
                    {formatCurrency(stats.total_commission)}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Platform earnings
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Avg Order Value
                  </CardTitle>
                  <Package className="h-5 w-5 text-gray-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-purple-600">
                    {formatCurrency(stats.average_order_value)}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Per order</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters & Actions */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <Input
                      placeholder="Search by order number, table, customer..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg bg-white"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="preparing">Preparing</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <Button onClick={() => fetchData(1)} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
                <Button className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Orders Table */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Orders</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12">
                  <XCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No orders found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Order ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Restaurant
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Customer
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Items
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Commission
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Time
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {orders.map((order) => (
                        <tr key={order.order_id} className="hover:bg-gray-50">
                          <td className="px-4 py-4">
                            <div className="font-medium text-gray-900">
                              {order.order_number ||
                                order.order_id.substring(0, 8)}
                            </div>
                            {order.table_number && (
                              <div className="text-xs text-gray-500">
                                Table {order.table_number}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm font-medium text-gray-900">
                              {order.restaurant_name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {order.order_type}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900">
                            {order.customer_name || "-"}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900">
                            {order.items_count} items
                          </td>
                          <td className="px-4 py-4">
                            <div className="font-semibold text-gray-900">
                              {formatCurrency(order.total_amount)}
                            </div>
                            {order.discount_amount > 0 && (
                              <div className="text-xs text-red-600">
                                -{formatCurrency(order.discount_amount)}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="font-semibold text-blue-600">
                              {formatCurrency(order.commission_amount)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {order.commission_percentage}%
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {getStatusBadge(order.status)}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">
                            {formatDateTime(order.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {total > LIMIT && (
                    <div className="flex justify-between items-center mt-6">
                      <p className="text-sm text-gray-600">
                        Showing {(page - 1) * LIMIT + 1} –{" "}
                        {Math.min(page * LIMIT, total)} of {total} orders
                      </p>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          disabled={page === 1}
                          onClick={() => fetchData(page - 1)}
                        >
                          Previous
                        </Button>

                        <Button
                          variant="outline"
                          disabled={page * LIMIT >= total}
                          onClick={() => fetchData(page + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </SuperAdminLayout>
  );
}
